import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell, ChefHat, Maximize2, Minimize2, RefreshCw } from "lucide-react";
import AdminOrdersService from "../../../Modules/Services/admin.orders.service";
import { formatRuPhoneInput } from "../../../Modules/Utils/phoneRu";

const POLL_MS = 4000;

const STATUS_TITLE = {
  new: "Новые",
  preparing: "Готовятся",
  ready: "Готовы к выдаче",
};

function formatRub(value) {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

const toDateAssumingYakutsk = (value) => {
  if (value instanceof Date) return value;
  if (typeof value === "string") {
    const s = value.trim();
    const m = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(\.\d+)?$/);
    if (m) return new Date(`${m[1]}T${m[2]}${m[3] || ""}+09:00`);
    return new Date(s);
  }
  return new Date(value);
};

function formatTime(v) {
  try {
    const d = toDateAssumingYakutsk(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return new Intl.DateTimeFormat("ru-RU", {
      timeZone: "Asia/Yakutsk",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(d);
  } catch {
    return String(v);
  }
}

function groupByStatus(list) {
  const g = { new: [], preparing: [], ready: [] };
  for (const o of list) {
    if (g[o.status]) g[o.status].push(o);
  }
  return g;
}

/** Короткий звук «как в очереди заказов» без внешних файлов */
function playKitchenChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    gain.gain.value = 0.12;

    const beep = (freq, t0, dur) => {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = freq;
      osc.connect(gain);
      osc.start(t0);
      osc.stop(t0 + dur);
    };

    const now = ctx.currentTime;
    beep(880, now, 0.12);
    beep(1174, now + 0.14, 0.14);

    ctx.resume().catch(() => {});
    window.setTimeout(() => {
      try {
        ctx.close();
      } catch {
        /* ignore */
      }
    }, 600);
  } catch {
    /* ignore */
  }
}

function notifyNewOrder(order) {
  if (typeof Notification === "undefined") return;
  if (Notification.permission !== "granted") return;
  try {
    const body =
      order.order_type === "delivery"
        ? `Доставка · ${formatRub(order.total_sum)}`
        : `Самовывоз · ${formatRub(order.total_sum)}`;
    new Notification(`Новый заказ №${order.id}`, {
      body,
      tag: `kitchen-order-${order.id}`,
      requireInteraction: false,
    });
  } catch {
    /* ignore */
  }
}

function OrderCard({ order, busy, onAdvance }) {
  const items = order.items || [];
  return (
    <article className="kitchen-card">
      <div className="kitchen-card__head">
        <span className="kitchen-card__id">№{order.id}</span>
        <span className="kitchen-card__time">{formatTime(order.created_at)}</span>
      </div>
      <div className="kitchen-card__meta">
        {order.order_type === "delivery" ? (
          <span className="kitchen-tag kitchen-tag--delivery">Доставка</span>
        ) : (
          <span className="kitchen-tag kitchen-tag--pickup">Самовывоз</span>
        )}
        <span className="kitchen-card__phone">{formatRuPhoneInput(order.phone) || order.phone}</span>
      </div>
      {order.order_type === "delivery" && order.address && (
        <div className="kitchen-card__addr">{order.address}</div>
      )}
      {order.order_type === "pickup" && order.pickup_place && (
        <div className="kitchen-card__addr">Место: {order.pickup_place}</div>
      )}
      {order.pickup_at && (
        <div className="kitchen-card__pickup">Ко времени: {order.pickup_at}</div>
      )}
      <ul className="kitchen-card__items">
        {items.map((it) => {
          const opt = it.size || it.weight ? ` (${it.size || it.weight})` : "";
          return (
            <li key={it.id}>
              <span className="kitchen-card__qty">{it.quantity}×</span>
              <span className="kitchen-card__name">
                {it.product_name}
                {opt}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="kitchen-card__foot">
        <span>{order.total_items} поз.</span>
        <strong>{formatRub(order.total_sum)}</strong>
      </div>
      <div className="kitchen-card__actions">
        {order.status === "new" && (
          <button
            type="button"
            className="btn btn--primary kitchen-card__btn"
            disabled={busy}
            onClick={() => onAdvance(order.id, "preparing")}
          >
            В работу
          </button>
        )}
        {order.status === "preparing" && (
          <button
            type="button"
            className="btn btn--primary kitchen-card__btn"
            disabled={busy}
            onClick={() => onAdvance(order.id, "ready")}
          >
            Готов к выдаче
          </button>
        )}
        {order.status === "ready" && (
          <button
            type="button"
            className="btn btn--primary kitchen-card__btn kitchen-card__btn--done"
            disabled={busy}
            onClick={() => onAdvance(order.id, "completed")}
          >
            Выдан
          </button>
        )}
      </div>
    </article>
  );
}

const KitchenBoard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [fs, setFs] = useState(false);

  const prevIdsRef = useRef(null);
  const rootRef = useRef(null);

  const fetchBoard = useCallback(async () => {
    try {
      const res = await AdminOrdersService.getKitchenBoard();
      if (res?.success) {
        const list = res.data?.items || [];
        setFetchError(null);

        if (prevIdsRef.current !== null) {
          const prev = prevIdsRef.current;
          for (const o of list) {
            if (!prev.has(o.id) && o.status === "new") {
              playKitchenChime();
              notifyNewOrder(o);
            }
          }
        }
        prevIdsRef.current = new Set(list.map((o) => o.id));

        setOrders(list);
      }
    } catch (e) {
      console.error("Kitchen board fetch:", e);
      setFetchError("Нет связи с сервером");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBoard();
    const id = window.setInterval(fetchBoard, POLL_MS);
    return () => window.clearInterval(id);
  }, [fetchBoard]);

  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const onFsChange = () => setFs(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const requestAlerts = async () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        await ctx.resume();
        await ctx.close();
      }
    } catch {
      /* ignore */
    }
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      await Notification.requestPermission();
    }
    playKitchenChime();
  };

  const toggleFullscreen = async () => {
    const el = rootRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch {
      /* ignore */
    }
  };

  const onAdvance = async (orderId, nextStatus) => {
    try {
      setUpdatingId(orderId);
      const res = await AdminOrdersService.patchOrderStatus(orderId, nextStatus);
      if (res?.success) await fetchBoard();
    } catch (e) {
      console.error("patch status:", e);
    } finally {
      setUpdatingId(null);
    }
  };

  const grouped = groupByStatus(orders);
  const clock = new Intl.DateTimeFormat("ru-RU", {
    timeZone: "Asia/Yakutsk",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date(nowTick));

  const colKeys = ["new", "preparing", "ready"];

  return (
    <div className="kitchen-board" ref={rootRef}>
      <header className="kitchen-board__top">
        <div className="kitchen-board__brand">
          <ChefHat size={28} strokeWidth={2} aria-hidden />
          <div>
            <h1 className="kitchen-board__title">Заказы · кухня</h1>
            <p className="kitchen-board__subtitle">
              Обновление каждые {POLL_MS / 1000} с · Якутск · {clock}
            </p>
          </div>
        </div>
        <div className="kitchen-board__toolbar">
          {fetchError && <span className="kitchen-board__warn">{fetchError}</span>}
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => fetchBoard()}
            title="Обновить сейчас"
          >
            <RefreshCw size={16} strokeWidth={2} style={{ marginRight: 6 }} />
            Обновить
          </button>
          <button type="button" className="btn btn--ghost btn--small" onClick={requestAlerts}>
            <Bell size={16} strokeWidth={2} style={{ marginRight: 6 }} />
            Звук и уведомления
          </button>
          <button type="button" className="btn btn--ghost btn--small" onClick={toggleFullscreen}>
            {fs ? (
              <>
                <Minimize2 size={16} strokeWidth={2} style={{ marginRight: 6 }} />
                Окно
              </>
            ) : (
              <>
                <Maximize2 size={16} strokeWidth={2} style={{ marginRight: 6 }} />
                Полный экран
              </>
            )}
          </button>
        </div>
      </header>

      {loading && orders.length === 0 ? (
        <div className="admin-spinner kitchen-board__loading">Загрузка заказов…</div>
      ) : (
        <div className="kitchen-board__columns">
          {colKeys.map((key) => (
            <section key={key} className={`kitchen-column kitchen-column--${key}`}>
              <h2 className="kitchen-column__title">
                {STATUS_TITLE[key]}
                <span className="kitchen-column__count">{grouped[key].length}</span>
              </h2>
              <div className="kitchen-column__list">
                {grouped[key].length === 0 ? (
                  <p className="kitchen-column__empty">Пусто</p>
                ) : (
                  grouped[key].map((o) => (
                    <OrderCard
                      key={o.id}
                      order={o}
                      busy={updatingId === o.id}
                      onAdvance={onAdvance}
                    />
                  ))
                )}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default KitchenBoard;
